import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { describeOffer } from '@affily/shared/commission';
import { bpsToPercent, percentToBps } from '@affily/shared/money';
import { CURRENCIES, CURRENCY_LABELS } from '@affily/shared/constants';
import { api } from '../../lib/api.js';
import { rule } from '../../lib/fieldRules.js';
import { useFetch } from '../../hooks/useFetch.js';
import { useBrand } from '../../hooks/useBrand.jsx';
import { isEmailUnverifiedError } from '../../hooks/useEmailVerification.js';
import { PROGRAM_TEMPLATES, DEFAULT_TEMPLATE_KEY, applyTemplate } from '@affily/shared/templates';
import { Seo } from '../../components/Seo.jsx';
import { Stepper } from '../../components/Stepper.jsx';
import { ConfirmEmailNotice } from '../../components/ConfirmEmailNotice.jsx';
import { StoreConnect } from '../../components/StoreConnect.jsx';
import { BillingSetup } from '../../components/BillingSetup.jsx';
import { TemplateSelect } from '../../components/TemplateSelect.jsx';
import { SetupCallLink } from '../../components/SetupCall.jsx';
import { Button, Field, Input, Select, Alert, Badge, Card, Spinner } from '../../components/ui.jsx';

const STEPS = [
  { key: 'company', label: 'Company' },
  { key: 'type', label: 'Program type' },
  { key: 'program', label: 'Your program' },
  { key: 'tracking', label: 'Tracking' },
  { key: 'billing', label: 'Payouts card' },
  { key: 'done', label: 'Done' },
];
const STEP_KEYS = STEPS.map((s) => s.key);

/** Server step names -> the wizard step that finishes them. */
const SERVER_STEP = {
  profile: 'company',
  program: 'type',
  tracking: 'tracking',
  billing: 'billing',
};

/** What "Start from scratch" means: a plain percentage program. */
const SCRATCH = {
  commissionType: 'percent_of_sale',
  rateBps: 1000,
  recurringMode: 'one_time',
  cookieWindowDays: 30,
  payoutSchedule: 'net30',
  autoApprove: false,
};

/**
 * What feeds a program its sales: a webhook secret pasted by hand, or a store
 * connected in one click. Returns the platform names, e.g. ['woocommerce'].
 */
function trackedBy(integration) {
  const manual = Object.entries(integration?.platforms ?? {})
    .filter(([, p]) => p.connected)
    .map(([k]) => k);
  const oneClick = (integration?.connections ?? [])
    .filter((c) => c.status === 'active')
    .map((c) => c.platform);
  return [...new Set([...manual, ...oneClick])];
}

/**
 * Brand onboarding: company, program, tracking, payouts card.
 *
 * Every step after the company is skippable and nothing is saved on "Skip".
 * "Finish later" just leaves — whatever was created stays created, and coming
 * back (or following the checklist on the dashboard) resumes at the first thing
 * still undone.
 */
export default function BrandWizard() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const brands = useBrand();

  const [boot, setBoot] = useState({ loading: true });
  const [step, setStepState] = useState(null);
  const [brand, setBrand] = useState(null);
  const [program, setProgram] = useState(null);
  const [createdHere, setCreatedHere] = useState(false);
  const [templateKey, setTemplateKey] = useState(null);
  const [complete, setComplete] = useState([]);
  const [skipped, setSkipped] = useState([]);

  const go = useCallback(
    (key) => {
      setStepState(key);
      setParams({ step: key }, { replace: true });
      window.scrollTo?.(0, 0);
    },
    [setParams],
  );

  const markDone = (key) => {
    setComplete((c) => (c.includes(key) ? c : [...c, key]));
    setSkipped((s) => s.filter((k) => k !== key));
  };
  const markSkipped = (key) => {
    setSkipped((s) => (s.includes(key) ? s : [...s, key]));
  };

  // ── Resume ────────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const get = (path) => api.get(path).catch(() => null);

      const [profileRes, programsRes, onboarding] = await Promise.all([
        get('/brands/me/profile'),
        get('/programs/mine/list'),
        get('/onboarding'),
      ]);
      if (cancelled) return;

      const b = profileRes?.brand ?? null;
      const server = onboarding?.brand ?? null;
      // A brand whose only program is a draft (publish fell back to draft while
      // the email was unconfirmed) is resumed onto THAT program, not an empty form.
      const draft = server?.draftProgramId
        ? (programsRes?.programs ?? []).find((p) => p.id === server.draftProgramId)
        : null;
      const latest = draft ?? programsRes?.programs?.[0] ?? null;

      let start;
      if (server) {
        // The server knows what is done (including tracking and billing, which
        // we can't cheaply see from here). Map its names onto ours.
        start = server.next ? (SERVER_STEP[server.next] ?? 'done') : 'done';
        if (server.templateKey) {
          setTemplateKey(server.templateKey);
          if (start === 'type') start = 'program';
        }
        // The program step is where a draft gets published; the type picker is
        // for making a new one.
        if (draft && start === 'type') start = 'program';
      } else if (!b) {
        // No onboarding endpoint: infer from what we can read directly.
        start = 'company';
      } else if (!latest) {
        start = 'type';
      } else {
        const [integration, billing] = await Promise.all([
          get(`/programs/${latest.id}/integration`),
          get('/stripe/billing'),
        ]);
        if (cancelled) return;
        const connected = trackedBy(integration).length > 0;
        if (!connected) start = 'tracking';
        else if (billing?.stripeConfigured && !billing.ready) start = 'billing';
        else start = 'done';
      }

      const asked = params.get('step');
      if (STEP_KEYS.includes(asked)) start = asked;

      // Never land on a step whose prerequisites don't exist.
      if (!b) start = 'company';
      else if (!latest && start === 'tracking') start = 'type';

      const done = [];
      if (b) done.push('company');
      // A draft is made but not finished: it still needs publishing.
      if (latest) done.push('type');
      if (latest && latest.status !== 'draft') done.push('program');
      setBrand(b);
      setProgram(latest);
      setComplete(done);
      setBoot({ loading: false });
      setStepState(start);
      setParams({ step: start }, { replace: true });
    })();
    return () => {
      cancelled = true;
    };
    // Resume runs once, on load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finishLater = () => navigate('/dashboard/brand');

  if (boot.loading || !step) {
    return (
      <Shell>
        <div className="flex min-h-64 items-center justify-center text-ink-faint">
          <Spinner />
        </div>
      </Shell>
    );
  }

  const skip = (key, to) => {
    markSkipped(key);
    go(to);
  };

  return (
    <Shell onFinishLater={finishLater}>
      <Seo title="Set up your brand" noindex />
      <Stepper steps={STEPS} current={step} complete={complete} skipped={skipped} />

      {step === 'company' && (
        <CompanyStep
          brand={brand}
          onCreated={async (b) => {
            setBrand(b);
            // Switch this tab to the new brand, so the program made next is its.
            await brands.reload(b.id).catch(() => {});
            markDone('company');
            go('type');
          }}
          onContinue={() => go('type')}
        />
      )}

      {step === 'type' && (
        <TypeStep
          existing={program}
          selected={templateKey}
          onPick={(key) => {
            setTemplateKey(key);
            markDone('type');
            if (key) api.patch('/onboarding/brand', { templateKey: key }).catch(() => {});
            go('program');
          }}
          onSkip={() => skip('type', 'program')}
        />
      )}

      {step === 'program' && (
        <ProgramStep
          brand={brand}
          templateKey={templateKey}
          program={program}
          createdHere={createdHere}
          onCreated={(p) => {
            setProgram(p);
            setCreatedHere(true);
          }}
          onContinue={() => {
            markDone('program');
            go('tracking');
          }}
          onSkip={() => skip('program', 'tracking')}
          onBackToType={() => go('type')}
        />
      )}

      {step === 'tracking' && (
        <TrackingStep
          program={program}
          onContinue={(connected) => {
            if (connected) markDone('tracking');
            else markSkipped('tracking');
            go('billing');
          }}
          onSkip={() => skip('tracking', 'billing')}
        />
      )}

      {step === 'billing' && (
        <BillingStep
          onContinue={async () => {
            // "Continue" is not "done": with Stripe unconfigured, or no card
            // saved, nothing was set up, and the Done page would say so.
            const billing = await api.get('/stripe/billing').catch(() => null);
            if (billing?.ready === true) markDone('billing');
            else markSkipped('billing');
            go('done');
          }}
          onSkip={() => skip('billing', 'done')}
        />
      )}

      {step === 'done' && <DoneStep brand={brand} program={program} skipped={skipped} />}
    </Shell>
  );
}

function Shell({ children, onFinishLater }) {
  return (
    <div className="min-h-dvh bg-paper">
      <header className="border-b border-rule bg-paper-raised">
        <div className="mx-auto flex h-14 max-w-3xl items-center justify-between px-4 sm:px-6">
          <Link to="/" className="font-semibold tracking-tight">
            Affily
          </Link>
          {onFinishLater && (
            <Button variant="ghost" size="sm" onClick={onFinishLater}>
              Finish later
            </Button>
          )}
        </div>
      </header>
      <main className="mx-auto max-w-3xl space-y-6 px-4 py-10 sm:px-6">
        {children}
        <SetupCallLink topic="setting up my brand on Affily" className="pt-4" />
      </main>
    </div>
  );
}

function Heading({ title, children }) {
  return (
    <div>
      <h1 className="text-3xl font-semibold tracking-tight">{title}</h1>
      {children && <p className="mt-2 max-w-xl text-ink-muted">{children}</p>}
    </div>
  );
}

function Footer({ children }) {
  return <div className="flex flex-wrap items-center gap-3 border-t border-rule pt-5">{children}</div>;
}

// ── Step: company ───────────────────────────────────────────────────────────

function CompanyStep({ brand, onCreated, onContinue }) {
  const [form, setForm] = useState({ companyName: '', website: '' });
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  if (brand) {
    return (
      <>
        <Heading title="Company">
          You&apos;re setting up {brand.companyName}. To set up a different brand,{' '}
          <Link to="/dashboard/brand/new" className="text-accent hover:underline">
            add it first
          </Link>
          .
        </Heading>
        <Card className="p-4">
          <p className="font-medium">{brand.companyName}</p>
          {brand.website && <p className="text-sm text-ink-muted">{brand.website}</p>}
        </Card>
        <Footer>
          <Button onClick={onContinue}>Continue</Button>
        </Footer>
      </>
    );
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    setFieldErrors({});
    try {
      const res = await api.post('/brands', {
        companyName: form.companyName,
        website: form.website || undefined,
      });
      onCreated(res.brand);
    } catch (err) {
      setError(err.message);
      setFieldErrors(err.details ?? {});
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Heading title="Your company">
        What creators see before they apply. You can add a logo, a description and categories
        later.
      </Heading>
      <form onSubmit={submit} className="space-y-4">
        <Alert>{error}</Alert>
        <Field label="Company name" required error={fieldErrors.companyName} constraint={rule.max(120)}>
          <Input value={form.companyName} onChange={set('companyName')} required maxLength={120} />
        </Field>
        <Field
          label="Website"
          error={fieldErrors.website}
          constraint={`${rule.optional()} · ${rule.url()}`}
        >
          <Input
            type="url"
            value={form.website}
            onChange={set('website')}
            placeholder="https://example.com"
          />
        </Field>
        <Footer>
          <Button type="submit" disabled={busy}>
            {busy ? 'Saving…' : 'Save and continue'}
          </Button>
        </Footer>
      </form>
    </>
  );
}

// ── Step: program type ──────────────────────────────────────────────────────

function TypeStep({ existing, selected, onPick, onSkip }) {
  const [choice, setChoice] = useState(selected ?? DEFAULT_TEMPLATE_KEY);

  return (
    <>
      <Heading title="What kind of program?">
        A starting point, not a commitment: you can change every term afterwards.
      </Heading>

      {existing && (
        <Alert tone="caution">
          You already have a program (“{existing.title}”). Skip this step to carry on, or pick a
          type to make another.
        </Alert>
      )}

      <TemplateSelect
        value={choice}
        onChange={setChoice}
        scratchBlurb="A plain percentage-of-sale program you edit yourself."
      />

      <Footer>
        <Button onClick={() => onPick(choice)}>Continue</Button>
        <Button variant="outline" onClick={onSkip}>
          Skip
        </Button>
      </Footer>
    </>
  );
}

// ── Step: your program ──────────────────────────────────────────────────────

/** What the one editable "headline" field is, for a given set of terms. */
function headlineKind(terms) {
  if (terms.commissionType === 'gift_only') return 'gift';
  if (terms.commissionType === 'percent_of_sale') return 'percent';
  return 'flat';
}

function ProgramStep({
  brand,
  templateKey,
  program,
  createdHere,
  onCreated,
  onContinue,
  onSkip,
  onBackToType,
}) {
  const base = templateKey ? applyTemplate(templateKey) : SCRATCH;
  const kind = headlineKind(base);
  const template = PROGRAM_TEMPLATES.find((t) => t.key === templateKey);

  // No company name: the public slug is company + title, and repeating the
  // company in both gave "acme-acme-partner-program".
  const [title, setTitle] = useState('Partner program');
  const [destinationUrl, setDestinationUrl] = useState(brand?.website ?? '');
  const [currency, setCurrency] = useState('eur');
  const [percent, setPercent] = useState(
    base.rateBps != null ? String(bpsToPercent(base.rateBps)) : '10',
  );
  const [amount, setAmount] = useState(
    base.flatAmountCents != null ? String(base.flatAmountCents / 100) : '',
  );
  const [giftName, setGiftName] = useState(base.gifts?.[0]?.name ?? '');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [savedAsDraft, setSavedAsDraft] = useState(false);
  const [wantsNew, setWantsNew] = useState(false);
  const [needsConfirm, setNeedsConfirm] = useState(false);

  // An existing draft is finished, not duplicated.
  if (!createdHere && !wantsNew && program?.status === 'draft') {
    const publishDraft = async () => {
      setBusy(true);
      setError('');
      setNeedsConfirm(false);
      try {
        const res = await api.patch(`/programs/${program.id}`, { status: 'active' });
        // Same screen as a fresh publish: "live", then Continue.
        onCreated(res.program);
      } catch (err) {
        if (isEmailUnverifiedError(err)) setNeedsConfirm(true);
        else setError(err.message);
      } finally {
        setBusy(false);
      }
    };

    return (
      <>
        <Heading title="Publish your program">
          You have a draft, so there is nothing to create again. Publishing makes its terms
          public.
        </Heading>
        <Alert>{error}</Alert>
        {needsConfirm && (
          <div className="space-y-2">
            <Alert tone="caution">
              Confirm your email first. We can only publish a program once it’s confirmed; the draft
              stays as it is.
            </Alert>
            <ConfirmEmailNotice />
          </div>
        )}
        <Card className="p-4">
          <p className="text-sm text-ink-muted">You have a draft:</p>
          <p className="font-medium">{program.title}</p>
          <p className="text-sm text-ink-muted">
            {program.offerSummary ?? describeOffer(program, program.gifts)}
          </p>
        </Card>
        <Footer>
          <Button onClick={publishDraft} disabled={busy}>
            {busy ? 'Publishing…' : 'Publish it'}
          </Button>
          <Button to={`/dashboard/brand/programs/${program.id}`} variant="outline">
            Edit in full editor
          </Button>
          <Button variant="ghost" onClick={onSkip}>
            Skip
          </Button>
          <button
            type="button"
            onClick={() => setWantsNew(true)}
            className="text-sm text-accent hover:underline"
          >
            Start a different program instead
          </button>
        </Footer>
      </>
    );
  }

  // Created in this session: show it, don't invite a duplicate.
  if (createdHere && program) {
    return (
      <>
        <Heading title={savedAsDraft || program.status !== 'active' ? 'Saved as a draft' : 'Your program is live'}>
          {program.status === 'active'
            ? 'It’s on the public directory now, with its terms in full.'
            : 'It isn’t public yet.'}
        </Heading>
        {program.status !== 'active' && (
          <div className="space-y-2">
            <Alert tone="caution">
              We saved this as a draft because your email isn’t confirmed yet. It stays private
              until you confirm and publish it.
            </Alert>
            <ConfirmEmailNotice />
          </div>
        )}
        <Card className="p-4">
          <p className="font-medium">{program.title}</p>
          <p className="text-sm text-ink-muted">{program.offerSummary ?? describeOffer(program, program.gifts)}</p>
        </Card>
        <Footer>
          <Button onClick={onContinue}>Continue</Button>
          <Button to={`/dashboard/brand/programs/${program.id}`} variant="outline">
            Open the full editor
          </Button>
        </Footer>
      </>
    );
  }

  function buildBody() {
    const overrides = { title, destinationUrl, currency };
    if (kind === 'percent') overrides.rateBps = percentToBps(Number(percent));
    if (kind === 'flat') overrides.flatAmountCents = Math.round(Number(amount) * 100);
    if (kind === 'gift') overrides.gifts = [{ ...(base.gifts?.[0] ?? {}), name: giftName }];
    return templateKey ? applyTemplate(templateKey, overrides) : { ...SCRATCH, ...overrides };
  }

  async function publish(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    setFieldErrors({});
    const body = buildBody();
    try {
      let res;
      try {
        res = await api.post('/programs', { ...body, status: 'active' });
      } catch (err) {
        if (!isEmailUnverifiedError(err)) throw err;
        // Unconfirmed email: the server won't publish. Keep their work as a draft.
        res = await api.post('/programs', { ...body, status: 'draft' });
        setSavedAsDraft(true);
      }
      onCreated(res.program);
    } catch (err) {
      setError(err.message);
      setFieldErrors(err.details ?? {});
    } finally {
      setBusy(false);
    }
  }

  let preview = null;
  try {
    const body = buildBody();
    preview = describeOffer(body, body.gifts);
    if (preview.includes('NaN')) preview = null;
  } catch {
    preview = null;
  }

  // Scratch is asked for by name: with no template the editor starts from the default one.
  const editorLink = `/dashboard/brand/programs/new?template=${templateKey ?? 'scratch'}`;
  const mappedKeys = ['title', 'destinationUrl', 'currency', 'rateBps', 'flatAmountCents', 'gifts'];
  const otherErrors = Object.entries(fieldErrors).filter(
    ([k]) => !mappedKeys.some((m) => k === m || k.startsWith(`${m}.`)),
  );

  return (
    <>
      <Heading title="Your program">
        {template ? `Based on “${template.name}”. ` : 'Starting from scratch. '}
        Just the essentials — everything else comes from the template and can be changed in the
        full editor.
      </Heading>

      <form onSubmit={publish} className="space-y-4">
        <Alert>{error}</Alert>
        {otherErrors.length > 0 && (
          <Alert>
            {otherErrors.map(([k, v]) => (
              <span key={k} className="block">
                {v}
              </span>
            ))}
          </Alert>
        )}

        <Field label="Program title" required error={fieldErrors.title} constraint={rule.max(140)}>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={140} />
        </Field>

        <Field
          label="Destination URL"
          required
          error={fieldErrors.destinationUrl}
          constraint={rule.url()}
          hint="Where a creator’s link sends people."
        >
          <Input
            type="url"
            value={destinationUrl}
            onChange={(e) => setDestinationUrl(e.target.value)}
            required
            placeholder="https://example.com"
          />
        </Field>

        <Field label="Currency" required error={fieldErrors.currency} hint="Everything on this program is paid in it.">
          <Select value={currency} onChange={(e) => setCurrency(e.target.value)}>
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {CURRENCY_LABELS[c]}
              </option>
            ))}
          </Select>
        </Field>

        {kind === 'percent' && (
          <Field
            label="Commission (% of each sale)"
            required
            error={fieldErrors.rateBps}
            constraint={rule.percent()}
          >
            <Input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={percent}
              onChange={(e) => setPercent(e.target.value)}
              required
            />
          </Field>
        )}
        {kind === 'flat' && (
          <Field
            label={
              base.commissionType === 'flat_per_post'
                ? `Fee per approved post (${currency.toUpperCase()})`
                : `Amount per conversion (${currency.toUpperCase()})`
            }
            required
            error={fieldErrors.flatAmountCents}
            constraint={rule.money()}
          >
            <Input
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
          </Field>
        )}
        {kind === 'gift' && (
          <Field
            label="Gift"
            required
            error={fieldErrors['gifts.0.name'] ?? fieldErrors.gifts}
            constraint={rule.max(80)}
            hint="What creators receive. No commission is paid on this kind of program."
          >
            <Input value={giftName} onChange={(e) => setGiftName(e.target.value)} required maxLength={80} />
          </Field>
        )}

        <p className="text-sm text-ink-muted">
          {preview && <Badge tone="neutral">{preview}</Badge>} Cookie window{' '}
          {base.cookieWindowDays} {base.cookieWindowDays === 1 ? 'day' : 'days'}
          {base.commissionType !== 'gift_only' && `, paid ${base.payoutSchedule}`}
          {base.autoApprove ? ', creators approved automatically.' : ', you approve each creator.'}
        </p>

        <Footer>
          <Button type="submit" disabled={busy}>
            {busy ? 'Publishing…' : 'Publish'}
          </Button>
          <Button variant="outline" onClick={onSkip} type="button">
            Skip
          </Button>
          <Button variant="ghost" onClick={onBackToType} type="button">
            Back
          </Button>
          <Link to={editorLink} className="text-sm text-accent hover:underline">
            Open the full editor
          </Link>
        </Footer>
        <p className="text-xs text-ink-faint">
          Publishing makes the terms public. If your email isn’t confirmed yet we’ll save a draft
          instead.
        </p>
      </form>
    </>
  );
}

// ── Step: tracking ──────────────────────────────────────────────────────────

function TrackingStep({ program, onContinue, onSkip }) {
  const integration = useFetch(program ? `/programs/${program.id}/integration` : null, {
    skip: !program,
  });

  if (!program) {
    return (
      <>
        <Heading title="Tracking">
          Tracking is set up per program, and you haven’t made one yet.
        </Heading>
        <Footer>
          <Button onClick={onSkip}>Skip for now</Button>
        </Footer>
      </>
    );
  }

  const int = integration.data;
  const connected = trackedBy(int).length > 0;

  return (
    <>
      <Heading title="How will we see a sale?">
        Without this, clicks are counted but sales aren’t, and nobody earns a commission. Three
        ways, best first:
      </Heading>

      <ol className="list-inside list-decimal space-y-1.5 text-sm text-ink-muted">
        <li>
          <span className="font-medium text-ink">Connect your store.</span> Approve Affily on
          your platform (or paste one webhook secret): no code, can’t be blocked.
        </li>
        <li>
          <span className="font-medium text-ink">Signed server-to-server call.</span> A few lines
          on your server; right for anything that pays money.
        </li>
        <li>
          <span className="font-medium text-ink">Browser tag.</span> Last resort: it’s blockable
          and loses roughly 20–40% of conversions.
        </li>
      </ol>

      {integration.loading && (
        <div className="text-ink-faint">
          <Spinner />
        </div>
      )}
      {integration.error && <Alert>{integration.error.message}</Alert>}

      {int && (
        <div className="space-y-8">
          <StoreConnect
            programId={program.id}
            platforms={int.platforms}
            stacks={int.stacks ?? []}
            onChanged={integration.reload}
          />
        </div>
      )}

      <Footer>
        <Button onClick={() => onContinue(connected)}>{connected ? 'Continue' : 'Continue without connecting'}</Button>
        {!connected && (
          <Button variant="outline" onClick={onSkip}>
            Skip
          </Button>
        )}
        <Link
          to={`/dashboard/brand/programs/${program.id}/actions`}
          className="text-sm text-accent hover:underline"
        >
          More tracking options
        </Link>
      </Footer>
    </>
  );
}

// ── Step: payouts card ──────────────────────────────────────────────────────

function BillingStep({ onContinue, onSkip }) {
  return (
    <>
      <Heading title="Add a payment method">
        Affily doesn’t front commissions, so a card lets us pay creators when you approve a payout.{' '}
        <span className="font-medium text-ink">
          Nothing is charged until you settle a payout.
        </span>{' '}
        Adding a card only saves it.
      </Heading>

      <p className="text-sm text-ink-muted">
        The card is entered on Stripe’s own page. Afterwards Stripe returns you to your billing
        page, and you can pick this setup back up from your dashboard.
      </p>

      <BillingSetup />

      <Footer>
        <Button onClick={onContinue}>Continue</Button>
        <Button variant="outline" onClick={onSkip}>
          Skip
        </Button>
      </Footer>
    </>
  );
}

// ── Step: done ──────────────────────────────────────────────────────────────

function DoneStep({ brand, program, skipped }) {
  const integration = useFetch(program ? `/programs/${program.id}/integration` : null, {
    skip: !program,
  });
  const billing = useFetch('/stripe/billing');

  const connected = trackedBy(integration.data);
  const loading = integration.loading || billing.loading;
  const live = program?.status === 'active';

  const rows = [
    {
      label: 'Company',
      ok: Boolean(brand),
      text: brand ? brand.companyName : 'Not added yet',
    },
    {
      label: 'Program',
      ok: Boolean(program),
      text: program
        ? `${program.title} — ${live ? 'live in the directory' : 'saved as a draft, not public'}`
        : skipped.includes('program') || skipped.includes('type')
          ? 'Skipped'
          : 'None yet',
    },
    {
      label: 'Tracking',
      ok: connected.length > 0,
      text: !program
        ? 'Needs a program first'
        : loading
          ? 'Checking…'
          : connected.length
            ? `Connected: ${connected.join(', ')}`
            : 'Not connected yet. Clicks are counted, sales aren’t.',
    },
    {
      label: 'Payouts card',
      ok: Boolean(billing.data?.ready),
      text: loading
        ? 'Checking…'
        : billing.data?.ready
          ? `On file — ${billing.data.paymentMethod}`
          : 'Not added. You’ll need one before you can pay a creator.',
    },
  ];

  return (
    <>
      <Heading title="That’s the setup">
        Here is where things stand. Anything not done is still available from your dashboard.
      </Heading>

      <ul className="divide-y divide-rule rounded-lg border border-rule bg-paper-raised">
        {rows.map((r) => (
          <li key={r.label} className="flex items-start gap-3 px-4 py-3">
            <Badge tone={r.ok ? 'positive' : 'neutral'}>{r.ok ? 'Done' : 'To do'}</Badge>
            <div>
              <p className="text-sm font-medium">{r.label}</p>
              <p className="text-sm text-ink-muted">{r.text}</p>
            </div>
          </li>
        ))}
      </ul>

      {program && !live && (
        <div className="space-y-2">
          <Alert tone="caution">
            Your program is a draft, so creators can’t see it yet. Confirm your email, then publish
            it from the editor.
          </Alert>
          <ConfirmEmailNotice />
        </div>
      )}

      <Footer>
        <Button to="/dashboard/brand">Go to your dashboard</Button>
        {program && (
          <Button to={`/dashboard/brand/programs/${program.id}`} variant="outline">
            Edit program
          </Button>
        )}
        {live && program.slug && (
          <Button to={`/programs/${program.slug}`} variant="outline">
            View public page
          </Button>
        )}
        <Button to="/dashboard/brand/creators" variant="ghost">
          Find creators
        </Button>
      </Footer>
    </>
  );
}
